import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-dropdown-select";
import { useTranslation } from "react-i18next";
import PageTitle from "../../Components/PageTitle";
import Preloader from "../../Components/Preloader";
import { logout } from "../../redux/actions/AuthActions";
import { getMasterUserTypeWalletList, getMasterUsertypeUserWiseList } from "../../redux/actions/MasterActions";
import { useNavigate } from "react-router-dom";
import RoundedPagination from "../../Components/Paginate";

const balanceFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
});

const formatBalanceValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "--";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value;
  }

  return balanceFormatter.format(numericValue);
};

const resolveEmployeeName = (row) =>
  row?.name ??
  row?.employee_name ??
  row?.full_name ??
  row?.user_name ??
  row?.username ??
  "--";

const resolveDesignation = (row) =>
  row?.designation ??
  row?.designation_name ??
  row?.role ??
  row?.role_name ??
  row?.adminUserType?.name ??
  row?.position ??
  "--";

const resolveBalance = (row) =>
  formatBalanceValue(row?.wallet_balance);

const UserWalletIndex = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userTypeId, setUserTypeId] = useState(0);
  const [page, setPage] = useState(1);
  const [paginate, setPaginate] = useState(10);

  const {
    AuthReducer: { auth },
    masterReducer: {
      loadingMaster,
      masterUsertypeLists,
      masterUserTypeWalletList,
      masterUserTypeWalletCount,
      errorsMaster,
    },
  } = useSelector((state) => state);

  useEffect(() => {
    if (auth?.token) {
      dispatch(getMasterUsertypeUserWiseList(auth.token));
    }
  }, [auth?.token, dispatch]);

  console.log("masterUserTypeWalletCount", masterUserTypeWalletCount);

  useEffect(() => {
    if (auth?.token && userTypeId !== null && userTypeId !== undefined) {
      dispatch(getMasterUserTypeWalletList(auth.token, userTypeId, paginate, page));
    }
  }, [auth?.token, userTypeId, paginate, page, dispatch]);

  useEffect(() => {
    if (errorsMaster && Object.keys(errorsMaster).length > 0 && errorsMaster?.status === 401) {
      dispatch(logout());
      toast.error(errorsMaster?.data?.message || "Your session has expired.");
    }
  }, [errorsMaster, dispatch]);

  const dropdownUserTypes = useMemo(() => {
    const normalized = Array.isArray(masterUsertypeLists)
      ? masterUsertypeLists.map(({ id, name }) => ({
          id: Number(id),
          name,
        }))
      : [];
    const allOption = { id: 0, name: t("All Users") };
    return [allOption, ...normalized];
  }, [masterUsertypeLists, t]);

  const walletRows = useMemo(() => {
    if (userTypeId === null || userTypeId === undefined) {
      return [];
    }

    return Array.isArray(masterUserTypeWalletList) ? masterUserTypeWalletList : [];
  }, [userTypeId, masterUserTypeWalletList]);

  // Total records are managed by backend (similar to TripReport)
  const totalRecords = useMemo(() => {
    if (typeof masterUserTypeWalletCount === "number" && masterUserTypeWalletCount >= 0) {
      return masterUserTypeWalletCount;
    }
    return walletRows.length;
  }, [masterUserTypeWalletCount, walletRows.length]);

  const totalPages = useMemo(() => {
    const pages = Math.ceil(totalRecords / paginate);
    return pages > 0 ? pages : 1;
  }, [totalRecords, paginate]);

  const handlePaginateChange = (event) => {
    const value = event.target.value;
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed) && parsed > 0) {
      setPaginate(parsed);
      setPage(1);
    }
  };

  const isTableLoading =
    loadingMaster && userTypeId !== null && userTypeId !== undefined;

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: "Attendance", path: "/Attendance/Daily", active: false },
          { label: "User Wallet", path: "/Attendance/UserWallet", active: true },
        ]}
        title="User Wallet"
      />

      <Row>
        <Col>
          <Card>
            <Card.Body>
              <Row className="g-3 align-items-end mb-4">
                <Col lg={3}>
                  <Form.Group>
                    <Form.Label>{t("User Type")}</Form.Label>
                    <Select
                      name="userTypeId"
                      options={dropdownUserTypes}
                      values={dropdownUserTypes.filter(
                        (option) => option?.id === Number(userTypeId)
                      )}
                      labelField="name"
                      valueField="id"
                      searchBy="name"
                      placeholder={t("Select...")}
                      multi={false}
                      disabled={loadingMaster && masterUsertypeLists.length === 0}
                      loading={loadingMaster && masterUsertypeLists.length === 0}
                      onChange={(selected) => {
                        if (!selected?.length) {
                          setUserTypeId(0);
                          return;
                        }
                        const parsedId = Number(selected[0]?.id);
                        setUserTypeId(Number.isNaN(parsedId) ? 0 : parsedId);
                      }}
                    />
                    {errorsMaster?.data?.user_type_id && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger mb-0">{errorsMaster?.data?.user_type_id}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <div className="table-responsive" style={{ maxHeight: "480px" }}>
                <Table hover striped bordered className="align-middle mb-0 table-sticky-header" size="lg">
                  <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <tr>
                      <th className="text-uppercase small text-muted fw-medium text-center">SR No.</th>
                      <th className="text-uppercase small text-muted fw-medium text-center">Employee Name</th>
                      <th className="text-uppercase small text-muted fw-medium text-center">Designation</th>
                      <th className="text-uppercase small text-muted fw-medium text-center">Email</th>
                      <th className="text-uppercase small text-muted fw-medium text-center">Phone</th>
                      <th className="text-uppercase small text-muted fw-medium text-center">Balance</th>
                      <th className="text-uppercase small text-muted fw-medium text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isTableLoading && (
                      <tr>
                        <td colSpan={7}>
                          <Preloader />
                        </td>
                      </tr>
                    )}
                    {!isTableLoading && walletRows.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4">
                          {userTypeId !== null && userTypeId !== undefined
                            ? "No wallet records found for this user type."
                            : "Please select a user type to view wallet details."}
                        </td>
                      </tr>
                    )}
                    {!isTableLoading &&
                      walletRows.map((employee, index) => (
                        <tr key={`${userTypeId}-${employee?.id ?? employee?.user_id}`}>
                          <td className="text-center">
                            {index + 1 + (page - 1) * paginate}
                          </td>
                          <td className="text-center">
                            <div className="fw-semibold text-dark">{resolveEmployeeName(employee)}</div>
                            {/* <div className="text-muted small">
                              {employee?.status ? employee.status : t("Status not available")}
                            </div> */}
                          </td>
                          <td className="text-center">
                            <div className="fw-semibold">{resolveDesignation(employee)}</div>
                            {/* <div className="text-muted small">
                              {employee?.adminUserType?.name ?? employee?.role_name ?? "--"}
                            </div> */}
                          </td>
                          <td className="text-center">
                            <div>{employee?.email ?? "--"}</div>
                            {/* {employee?.adminUserType?.name && (
                              <div className="text-muted small">{employee.adminUserType.name}</div>
                            )} */}
                          </td>
                          <td className="text-center">{employee?.phone ?? employee?.mobile ?? employee?.contact_number ?? "--"}</td>
                          <td className="text-center">
                            <span className="badge bg-soft-primary text-primary px-3 py-2">
                              {resolveBalance(employee)}
                            </span>
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              disabled={!employee?.id && !employee?.user_id}
                              onClick={() => {
                                const targetUserId = employee?.id ?? employee?.user_id;
                                if (!targetUserId) {
                                  toast.error("Unable to open wallet details for this record.");
                                  return;
                                }
                                navigate(`/User-Wallet-Details/${targetUserId}`, {
                                  state: {
                                    userTypeId,
                                    userName: resolveEmployeeName(employee),
                                    designation: resolveDesignation(employee),
                                  },
                                });
                              }}
                            >
                              Details
                            </Button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
              {!isTableLoading && walletRows.length > 0 && (
                <div className="mt-3 d-lg-flex align-items-center text-center pb-1">
                  <div className="d-inline-block me-3">
                    <Form.Label className="me-1 mb-0">Display :</Form.Label>
                    <Form.Select
                      className="d-inline-block w-auto"
                      value={String(paginate)}
                      onChange={handlePaginateChange}
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </Form.Select>
                  </div>
                  <span className="me-3">
                    Page <strong>{page}</strong> of{" "}
                    <strong>{totalPages}</strong>
                  </span>
                  <RoundedPagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={(pageNumber) => setPage(pageNumber)}
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default UserWalletIndex;

